import {
  array,
  InferInput,
  InferOutput,
  number,
  object,
  optional,
  pipe,
  safeParse,
  string,
  transform,
  ValiError,
} from 'valibot';
import {
  DEFAULT_MAX_RECOMMEND_CONTENTS,
  RecommendGroupSchema,
  RecommendList,
  RecommendListSchema,
} from '../apiSchema.ts';
import { getOffsetNumber } from '../utils.ts';

/**
 * Schema for the parameters of the `/v1/me/recommendations` endpoint.
 */
export const MeRecommendationsRequestArgsSchema = object({
  limit: number(),
  offset: optional(number()),
});

/**
 * Type for the parameters of the `/v1/me/recommendations` endpoint.
 */
export type MeRecommendationsRequestArgs = InferOutput<
  typeof MeRecommendationsRequestArgsSchema
>;

/**
 * Type for the API response of the `player.api.music('/v1/me/recommendations')`.
 */
export const MeRecommendationsAPISchema = object({
  data: object({
    data: array(RecommendListSchema),
    next: optional(string()),
  }),
});

export const MeRecommendGroupAPISchema = pipe(
  object({
    data: object({
      data: array(RecommendGroupSchema),
      next: optional(string()),
    }),
  }),
  transform(({ data: { data: recommendGroups, next } }) => {
    return {
      recommendGroups,
      next: next === undefined ? undefined : getOffsetNumber(next),
    };
  }),
);

export type RecommendGroupsResponse = InferOutput<
  typeof MeRecommendGroupAPISchema
>;

/**
 * Fetchs the recommendations (primary and secondary) for the user.
 * @param {MusicKit.MusicKitInstance} player The MusicKit instance.
 * @returns {Promise<RecommendList[]>} The response data.
 * @throws {ValiError} If the response is not valid.
 */
export const $getMeRecommendations = async (
  player: MusicKit.MusicKitInstance,
): Promise<RecommendList[]> => {
  const res = await player.api.music(
    '/v1/me/recommendations',
    {
      limit: 2,
      offset: 0,
    },
  ) as unknown as InferInput<typeof MeRecommendationsAPISchema>;

  const parsed = safeParse(MeRecommendationsAPISchema, res);
  if (!parsed.success) {
    throw new ValiError(parsed.issues);
  }

  return parsed.output.data.data;
};

/**
 * Fetchs the recommendation groups for the user.
 * @param {MusicKit.MusicKitInstance} player The MusicKit instance.
 * @param {Promise<MeRecommendationsRequestArgs>} params The request parameters.
 * @returns {Promise<RecommendGroup[]>} The response data.
 */
export const $getMeRecommendGroups = async (
  player: MusicKit.MusicKitInstance,
  params: MeRecommendationsRequestArgs,
): Promise<RecommendGroupsResponse> => {
  const p = safeParse(MeRecommendationsRequestArgsSchema, params);
  if (!p.success) {
    throw new ValiError(p.issues);
  }
  const limit = p.output.limit ?? DEFAULT_MAX_RECOMMEND_CONTENTS;
  if (limit <= 2) {
    return {
      recommendGroups: [],
      next: undefined,
    };
  }

  const offset = p.output.offset ?? 2;
  if (offset < 2) {
    throw new TypeError(
      `getRecommendGroups: offset must be greater than or equal to 2, but got ${offset}`,
    );
  }
  const res = await player.api.music(
    '/v1/me/recommendations',
    {
      limit: limit - 2,
      offset,
    },
  ) as unknown as InferInput<typeof MeRecommendGroupAPISchema>;

  const parsed = safeParse(MeRecommendGroupAPISchema, res);
  if (!parsed.success) {
    console.error(JSON.stringify(parsed.issues, null, 2));
    throw new ValiError(parsed.issues);
  }

  return parsed.output;
};
